import { t, tf } from "./i18n.js";
// ==========================================
// PROFIL-SCREEN
//
// Loest das alte Statistik-Modal ab - fuer das eigene Profil UND fuer
// fremde Spieler (Klick in den Bestenlisten ruft openPlayerProfile auf,
// nicht mehr das Modal aus stats.js).
//
// Aufbau: Kopf (Avatar, Name, Gesamtzahlen) + Reiter je Spielmodus.
//
// Es gibt zwei Arten von Tabellen:
//   "single"    - eine kumulierte Zeile pro Nutzer (stats_501, stats_secure)
//   "rows"      - eine Zeile pro Durchgang, Auswertung passiert hier
//                 (stats_cricket, stats_bobs, stats_rtw, stats_121, stats_random)
// ==========================================

let profileActiveTab = "501";
let profileData = {}; // tab -> geladene Rohdaten
let profileChart = null;

// null = eigenes Profil (Abfragen ueber currentUser.id).
// Sonst { name, userId|null } - ein fremdes Profil aus der Bestenliste.
// Ohne userId (unregistrierter Name) sind nur die "single"-Tabellen
// erreichbar, die eine name-Spalte haben.
let profileView = null;

// BUGFIX "Zurueck fuehrt ins Hauptmenue statt in die Statistiken":
// Wird das Profil aus der Bestenliste geoeffnet, merken wir uns den
// Herkunfts-Screen und kehren mit "Zurueck" dorthin zurueck.
let profileReturnScreen = null;

// Haengt den passenden Filter (eigene ID / fremde ID / fremder Name)
// an eine Supabase-Query an.
function profileFilter(query) {
  if (profileView) {
    return profileView.userId
      ? query.eq("user_id", profileView.userId)
      : query.eq("name", profileView.name);
  }
  return query.eq("user_id", currentUser.id);
}

// "rows"-Tabellen haben keine name-Spalte - ohne user_id gibt es dort
// nichts zu holen.
function profileRowsUnavailable() {
  return profileView !== null && !profileView.userId;
}

// Fuer heatmap.js: Die Wurf-Heatmap gibt es nur im eigenen Profil
// (KONZEPT_HEATMAP.md §6, Datenschutz) - und die RLS von dart_throws
// liesse fremde Zeilen ohnehin nicht durch.
function profileViewIstFremd() {
  return profileView !== null;
}
window.profileViewIstFremd = profileViewIstFremd;

const PROFILE_TABS = [
  { key: "501", label: "501", table: "stats_501", type: "single" },
  { key: "secure", label: "Secure", table: "stats_secure", type: "single" },
  { key: "cricket", label: "Cricket", table: "stats_cricket", type: "rows" },
  { key: "bobs", label: "Bob's 27", table: "stats_bobs", type: "rows" },
  { key: "rtw", label: "RTW", table: "stats_rtw", type: "rows" },
  { key: "121", label: "Catch 121", table: "stats_121", type: "rows" },
  { key: "random", label: "Random", table: "stats_random", type: "rows" },
  // Heatmap der getrackten Wuerfe (KONZEPT_HEATMAP.md P2/P3). Laedt und
  // rendert selbst (heatmap.js) - type "heatmap" ist deshalb in
  // loadProfileTab/renderProfileTab ein eigener Zweig und zaehlt NICHT
  // in die Gesamtzahlen (ein Wurf ist keine Partie).
  { key: "heatmap", label: "Heatmap", table: "dart_throws", type: "heatmap" },
];

// ------------------------------------------------------------------
// EINSTIEG
// ------------------------------------------------------------------
async function openProfileScreen(tab = null) {
  if (isGuest || !currentUser) {
    return showToast(t("js.profile.001"), "warning");
  }

  // Falls vorher ein fremdes Profil offen war: Cache verwerfen
  if (profileView !== null) {
    profileView = null;
    profileData = {};
  }
  // Eigenes Profil kommt aus der Navigation -> Zurueck geht ins Hauptmenue
  profileReturnScreen = null;

  await showProfileScreenInternal(tab);
}

// Fremdes Profil aus einer Bestenliste oeffnen. Der Name kommt URL-kodiert
// aus dem onclick-Attribut (Namen koennen Anfuehrungszeichen enthalten).
async function openPlayerProfile(encodedName, userId = "") {
  const name = decodeURIComponent(encodedName || "");
  if (!name) return;

  // Das eigene Profil? Dann die volle eigene Ansicht.
  if (!isGuest && currentUser && userId && userId === currentUser.id) {
    return openProfileScreen();
  }

  // Herkunft merken: Von der Statistiken-Seite aus geoeffnet?
  const statsScreen = document.getElementById("highscore-screen");
  profileReturnScreen =
    statsScreen && statsScreen.style.display !== "none"
      ? "highscore-screen"
      : null;

  profileView = { name: name, userId: userId || null };
  profileData = {};
  await showProfileScreenInternal(null);
}

async function showProfileScreenInternal(tab) {
  hideAllScreens();
  const screen = document.getElementById("profile-screen");
  if (screen) screen.style.display = "block";

  const menu = document.getElementById("main-nav");
  if (menu && menu.classList.contains("open")) toggleMenu();

  renderProfileHeader();
  renderProfileTabs();
  await switchProfileTab(tab || profileActiveTab);
  loadProfileTotals();
}

function closeProfileScreen() {
  if (profileChart) {
    profileChart.destroy();
    profileChart = null;
  }
  // Fremde Ansicht beenden, damit das naechste Oeffnen wieder das
  // eigene Profil mit frischem Cache zeigt.
  if (profileView !== null) {
    profileView = null;
    profileData = {};
  }

  // Zurueck zur Herkunft: Bestenliste bleibt Bestenliste (die Tabelle
  // steht dank SWR-Cache noch da), sonst wie gehabt ins Hauptmenue.
  if (profileReturnScreen) {
    const target = document.getElementById(profileReturnScreen);
    profileReturnScreen = null;
    if (target) {
      hideAllScreens();
      showScreenElement(target);
      return;
    }
  }

  goHome();
}

// ------------------------------------------------------------------
// KOPFBEREICH
// ------------------------------------------------------------------
function profileDisplayName() {
  return (
    currentUser?.user_metadata?.display_name ||
    currentUser?.user_metadata?.full_name ||
    currentUser?.email?.split("@")[0] ||
    t("js.app.009")
  );
}

// Durchschnitt als Stufe, nicht als Zahl.
//
// Der Dreidart-Durchschnitt kommt aus stats_501 - dieselbe Rechnung wie im
// 501-Reiter: (geworfene Punkte / geworfene Darts) * 3. Angezeigt wird der
// auf Fuenf abgerundete Wert, mindestens 20+. Ohne Daten bleibt das
// Abzeichen aus, statt eine 0 zu behaupten.
async function renderProfileAvg() {
  const badge = document.getElementById("profile-avg");
  if (!badge) return;
  badge.style.display = "none";
  // Ohne Login gibt es kein eigenes Profil - fremde Ansichten (profileView)
  // duerfen aber auch als Gast geladen werden.
  if (!profileView && (!currentUser || isGuest)) return;

  try {
    const { data } = await profileFilter(
      _supabase
        .from("stats_501")
        .select("total_score_thrown, total_darts_thrown"),
    ).maybeSingle();

    const darts = Number(data?.total_darts_thrown) || 0;
    const punkte = Number(data?.total_score_thrown) || 0;
    if (!darts) return;

    const schnitt = (punkte / darts) * 3;
    if (!Number.isFinite(schnitt) || schnitt <= 0) return;

    const stufe = Math.max(20, Math.floor(schnitt / 5) * 5);
    badge.textContent = stufe + "+";
    badge.style.display = "";
  } catch (err) {
    // Fehlende Tabelle oder Rechteproblem darf den Kopfbereich nicht stoppen
  }
}

function renderProfileHeader() {
  const name = profileView ? profileView.name : profileDisplayName();
  setProfileText("profile-name", name);

  // Bearbeiten-Knoepfe (Avatar, Name) nur auf dem eigenen Profil zeigen
  const avatarEditBtn = document.getElementById("profile-avatar-edit-btn");
  if (avatarEditBtn) avatarEditBtn.style.display = profileView ? "none" : "";
  const renameBtn = document.getElementById("profile-rename-btn");
  if (renameBtn) renameBtn.style.display = profileView ? "none" : "";
  // E-Mail bewusst nicht mehr anzeigen - sie gehoert in die
  // Einstellungen, nicht auf die Profilkarte.

  const img = document.getElementById("profile-avatar");
  if (img) {
    const fallback = `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(name)}`;
    // Sofort-Anzeige: hochgeladenes Bild aus den Session-Metadaten ->
    // generierter Fallback. Das ist nur der Platzhalter fuer den ersten
    // Frame - die Wahrheit kommt gleich aus der profiles-Tabelle.
    img.src = profileView
      ? fallback
      : currentUser?.user_metadata?.avatar_url || fallback;
    img.alt = tf("js.profile.013", { p1: name });
    img.onerror = () => {
      img.onerror = null;
      img.src = fallback;
    };

    // PROFILBILD-FIX 07.08.2026 (Aufgabe 4): Der echte Avatar wird IMMER
    // asynchron aus der profiles-Tabelle geladen - auch fuer das eigene
    // Profil. Vorher galten die user_metadata der gecachten localStorage-
    // Session als Quelle: Ein neues Profilbild landet aber sofort in
    // profiles, waehrend der Session-Cache erst beim naechsten Token-
    // Refresh nachzieht. Ergebnis war das "falsche Bild beim ersten
    // Oeffnen, richtig nach Reload". Jetzt gibt es genau EINE Quelle der
    // Wahrheit (profiles) - kein Workaround, sondern derselbe Weg, den
    // showMainApp in der Shell und das Freunde-Modal bereits gehen.
    const q = _supabase.from("profiles").select("avatar_url");
    (profileView
      ? profileView.userId
        ? q.eq("id", profileView.userId)
        : q.eq("name", profileView.name)
      : q.eq("id", currentUser?.id || "")
    )
      .maybeSingle()
      .then(({ data }) => {
        // Nur setzen, wenn dieselbe Ansicht noch offen ist
        const sameView = profileView
          ? profileView.name === name
          : profileView === null;
        if (data?.avatar_url && sameView) {
          img.src = data.avatar_url;
          // Session-Metadaten fuer diese Sitzung mitziehen, damit andere
          // Stellen (z.B. Namensfelder mit Avatar) denselben Stand sehen.
          if (!profileView && currentUser) {
            if (!currentUser.user_metadata) currentUser.user_metadata = {};
            currentUser.user_metadata.avatar_url = data.avatar_url;
          }
        }
      })
      .catch(() => {});
  }

  // "Dabei seit" gibt es nur fuers eigene Profil (auth-Datum ist fuer
  // fremde Spieler nicht einsehbar).
  const since =
    !profileView && currentUser?.created_at
      ? new Date(currentUser.created_at).toLocaleDateString("de-DE", {
          month: "long",
          year: "numeric",
        })
      : "";
  setProfileText("profile-since", since ? tf("js.profile.059", { p1: since }) : "");

  renderProfileAvg();
}

// Zaehlt ueber alle Modi, wie viele Partien gespielt wurden.
// LADEZEIT: Die 7 Tabellen werden parallel abgefragt (Promise.all) statt
// nacheinander - vorher summierten sich 7 Roundtrips zur spuerbaren Pause.
async function loadProfileTotals() {
  // Umbau 07.08.2026 (Aufgabe 3): Gesamtzahlen sind Statistik und stehen
  // nicht mehr auf der Profilkarte - ohne Zielelement nichts laden.
  if (!document.getElementById("profile-total-games")) return;
  // Nur Spiel-Reiter zaehlen - die Heatmap-Wuerfe sind keine Partien.
  const zaehlbar = PROFILE_TABS.filter((t) => t.type !== "heatmap");
  const counts = await Promise.all(
    zaehlbar.map(async (tab) => {
      try {
        if (tab.type === "single") {
          const { data } = await profileFilter(
            _supabase.from(tab.table).select("games_played"),
          ).maybeSingle();
          return (data && data.games_played) || 0;
        }
        if (profileRowsUnavailable()) return 0;
        const { count } = await profileFilter(
          _supabase.from(tab.table).select("id", { count: "exact", head: true }),
        );
        return count || 0;
      } catch (err) {
        // Fehlende Tabelle darf den Kopfbereich nicht blockieren
        console.warn(`Gesamtzahlen ${tab.table}:`, err?.message || err);
        return 0;
      }
    }),
  );

  const games = counts.reduce((a, b) => a + b, 0);
  const modes = counts.filter((c) => c > 0).length;

  setProfileText("profile-total-games", games);
  setProfileText("profile-total-modes", `${modes} / ${zaehlbar.length}`);
}

// ------------------------------------------------------------------
// REITER
// ------------------------------------------------------------------
function renderProfileTabs() {
  const bar = document.getElementById("profile-tabs");
  if (!bar || bar.dataset.built) return;

  PROFILE_TABS.forEach((tab) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "profile-tab";
    btn.dataset.tab = tab.key;
    btn.textContent = tab.label;
    btn.onclick = () => switchProfileTab(tab.key);
    bar.appendChild(btn);
  });
  bar.dataset.built = "1";
}

async function switchProfileTab(key) {
  // Umbau 07.08.2026 (Aufgabe 3): Die Statistik-Reiter leben jetzt auf der
  // eigenen Seite stats.html. Im Shell-Profil gibt es #profile-tabs nicht
  // mehr - dann hier aussteigen, statt Daten fuer nichts zu laden.
  if (!document.getElementById("profile-tabs")) return;
  const tab = PROFILE_TABS.find((t) => t.key === key);
  if (!tab) return;
  profileActiveTab = key;

  document.querySelectorAll("#profile-tabs .profile-tab").forEach((btn) => {
    const active = btn.dataset.tab === key;
    btn.classList.toggle("active", active);
    btn.setAttribute("aria-selected", active ? "true" : "false");
  });

  const body = document.getElementById("profile-tab-body");
  // GUI-Plan P3.11 (08.08.2026): Skeleton-Kacheln statt leerer Flaeche,
  // damit die Ansicht beim Eintreffen der Daten nicht springt.
  if (body)
    body.innerHTML =
      typeof skeletonKpiHtml === "function" ? skeletonKpiHtml(8) : "";
  setProfileText("profile-empty", "");

  const data = await loadProfileTab(tab);
  renderProfileTab(tab, data);
}

async function loadProfileTab(tab) {
  // Heatmap laedt und cached selbst (heatmap.js) - hier nichts zu tun.
  if (tab.type === "heatmap") return null;
  if (profileData[tab.key] !== undefined) return profileData[tab.key];

  try {
    if (tab.type === "single") {
      const { data, error } = await profileFilter(
        _supabase.from(tab.table).select("*"),
      ).maybeSingle();
      if (error) throw error;
      profileData[tab.key] = data || null;
    } else if (profileRowsUnavailable()) {
      // Unregistrierter Name: rows-Tabellen haben keine name-Spalte
      profileData[tab.key] = [];
    } else {
      const { data, error } = await profileFilter(
        _supabase.from(tab.table).select("*"),
      )
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      profileData[tab.key] = data || [];
    }
  } catch (err) {
    console.warn(`Profil ${tab.table}:`, err?.message || err);
    profileData[tab.key] = tab.type === "single" ? null : [];
  }
  return profileData[tab.key];
}

// ------------------------------------------------------------------
// KENNZAHLEN JE MODUS
// ------------------------------------------------------------------
window.num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
window.avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);

function buildProfileStats(tabKey, data) {
  if (tabKey === "501") return build501Stats(data);
  if (tabKey === "secure") return buildSecureStats(data);
  if (tabKey === "cricket") return buildCricketStats(data);
  if (tabKey === "bobs") return buildBobsStats(data);
  if (tabKey === "rtw") return buildRtwStats(data);
  if (tabKey === "121") return build121Stats(data);
  if (tabKey === "random") return buildRandomStats(data);
  return { kpis: [], chart: null };
}

function build501Stats(d) {
  if (!d) return { kpis: [], chart: null };
  const darts = num(d.total_darts_thrown);
  const games = num(d.games_played);
  return {
    kpis: [
      { label: t("js.profile.014"), value: games },
      {
        label: t("js.friends.031"),
        value: games ? Math.round((num(d.wins) / games) * 100) + " %" : "-",
      },
      {
        label: t("js.friends.029"),
        value: darts ? ((num(d.total_score_thrown) / darts) * 3).toFixed(2) : "-",
        accent: "var(--accent-green)",
      },
      {
        label: t("js.friends.030"),
        value: num(d.first9_darts)
          ? ((num(d.first9_score) / num(d.first9_darts)) * 3).toFixed(2)
          : "-",
        accent: "var(--accent-blue)",
      },
      { label: t("js.profile.035"), value: num(d.highest_finish) || "-" },
      { label: t("js.friends.012"), value: num(d.best_leg) || "-" },
      { label: "180er", value: num(d.count_180), accent: "var(--accent-green)" },
      { label: "140+", value: num(d.count_140) },
      { label: "100+", value: num(d.count_100) },
      { label: t("js.profile.036"), value: num(d.count_busts), accent: "var(--accent-red)" },
    ],
    chart: buildScoreDistribution(d.score_frequencies),
  };
}

function buildSecureStats(d) {
  if (!d) return { kpis: [], chart: null };
  const games = num(d.games_played);
  const rounds = num(d.rounds_played);
  return {
    kpis: [
      { label: t("js.profile.037"), value: games },
      { label: t("js.friends.013"), value: num(d.wins), accent: "var(--accent-green)" },
      {
        label: t("js.friends.031"),
        value: games ? Math.round((num(d.wins) / games) * 100) + " %" : "-",
      },
      { label: t("js.friends.032"), value: num(d.highscore), accent: "var(--accent)" },
      {
        label: t("js.profile.002"),
        value: games ? Math.round(num(d.total_points) / games) : "-",
      },
      {
        label: t("js.friends.033"),
        value: rounds ? Math.round((num(d.secure_count) / rounds) * 100) + " %" : "-",
        accent: "var(--accent-blue)",
      },
      {
        label: t("js.profile.015"),
        value: rounds ? Math.round((num(d.double_count) / rounds) * 100) + " %" : "-",
      },
      { label: t("js.profile.016"), value: rounds },
    ],
    chart: buildNumberChart(d.number_stats),
  };
}

function buildCricketStats(rows) {
  if (!rows.length) return { kpis: [], chart: null };
  const wins = rows.filter((r) =>
    (r.details || []).some((p) => p.is_win && p.name),
  ).length;
  const solo = rows.filter((r) => r.is_solo);
  const soloDarts = solo
    .map((r) => num((r.details || [])[0]?.darts))
    .filter((n) => n > 0);

  return {
    kpis: [
      { label: t("js.profile.038"), value: rows.length },
      { label: t("js.friends.013"), value: wins, accent: "var(--accent-green)" },
      { label: "Cricket", value: rows.filter((r) => r.variant === "cricket").length },
      { label: "Tactics", value: rows.filter((r) => r.variant === "tactics").length },
      { label: t("js.profile.017"), value: solo.length },
      {
        label: t("js.profile.018"),
        value: soloDarts.length ? Math.min(...soloDarts) + t("js.profile.019") : "-",
        accent: "var(--accent)",
      },
      {
        label: t("js.profile.020"),
        value: soloDarts.length ? Math.round(avg(soloDarts)) : "-",
      },
    ],
    chart: null,
  };
}

function buildBobsStats(rows) {
  if (!rows.length) return { kpis: [], chart: null };
  const scores = rows.map((r) => num(r.final_score));
  const wins = rows.filter((r) => r.is_win).length;
  return {
    kpis: [
      { label: t("js.profile.021"), value: rows.length },
      { label: t("js.profile.039"), value: wins, accent: "var(--accent-green)" },
      {
        label: t("js.profile.040"),
        value: Math.round((wins / rows.length) * 100) + " %",
      },
      { label: t("js.friends.032"), value: Math.max(...scores), accent: "var(--accent)" },
      { label: t("js.profile.041"), value: Math.round(avg(scores)) },
      { label: t("js.profile.042"), value: num(rows[0].final_score) },
    ],
    chart: {
      type: "line",
      labels: rows.slice(0, 20).reverse().map((_, i) => `#${i + 1}`),
      values: rows.slice(0, 20).reverse().map((r) => num(r.final_score)),
      title: t("js.profile.003"),
    },
  };
}

function buildRtwStats(rows) {
  if (!rows.length) return { kpis: [], chart: null };
  const pts = rows.map((r) => num(r.total_points));
  const byMode = (m) => rows.filter((r) => r.mode === m).length;
  return {
    kpis: [
      { label: t("js.profile.021"), value: rows.length },
      { label: t("js.profile.022"), value: Math.max(...pts), accent: "var(--accent)" },
      { label: t("js.profile.023"), value: avg(pts).toFixed(1) },
      { label: t("js.profile.043"), value: num(rows[0].total_points) },
      { label: t("js.profile.044"), value: byMode("single") },
      { label: t("js.profile.045"), value: byMode("double") },
      { label: t("js.profile.046"), value: byMode("triple") },
    ],
    chart: {
      type: "line",
      labels: rows.slice(0, 20).reverse().map((_, i) => `#${i + 1}`),
      values: rows.slice(0, 20).reverse().map((r) => num(r.total_points)),
      title: t("js.profile.047"),
    },
  };
}

function build121Stats(rows) {
  if (!rows.length) return { kpis: [], chart: null };
  const best = rows.map((r) => num(r.best_target));
  const won = rows.reduce((a, r) => a + num(r.levels_won), 0);
  const lost = rows.reduce((a, r) => a + num(r.levels_lost), 0);
  return {
    kpis: [
      { label: t("js.profile.021"), value: rows.length },
      { label: t("js.profile.004"), value: Math.max(...best), accent: "var(--accent)" },
      { label: t("js.profile.005"), value: Math.round(avg(best)) },
      { label: t("js.profile.024"), value: won, accent: "var(--accent-green)" },
      { label: t("js.profile.025"), value: lost, accent: "var(--accent-red)" },
      {
        label: t("js.profile.026"),
        value: won + lost ? Math.round((won / (won + lost)) * 100) + " %" : "-",
      },
    ],
    chart: {
      type: "line",
      labels: rows.slice(0, 20).reverse().map((_, i) => `#${i + 1}`),
      values: rows.slice(0, 20).reverse().map((r) => num(r.best_target)),
      title: t("js.profile.006"),
    },
  };
}

function buildRandomStats(rows) {
  if (!rows.length) return { kpis: [], chart: null };
  const hits = rows.reduce((a, r) => a + num(r.hits), 0);
  const tries = rows.reduce((a, r) => a + num(r.attempts), 0);
  const quotes = rows.map((r) => num(r.quote) * 100);
  return {
    kpis: [
      { label: t("js.profile.048"), value: rows.length },
      { label: t("js.profile.049"), value: tries },
      { label: t("js.profile.050"), value: hits, accent: "var(--accent-green)" },
      {
        label: t("js.profile.051"),
        value: tries ? Math.round((hits / tries) * 100) + " %" : "-",
        accent: "var(--accent-blue)",
      },
      {
        label: t("js.profile.052"),
        value: quotes.length ? Math.round(Math.max(...quotes)) + " %" : "-",
        accent: "var(--accent)",
      },
      {
        label: t("js.profile.027"),
        value: hits
          ? (rows.reduce((a, r) => a + num(r.darts_used), 0) / hits).toFixed(1)
          : "-",
      },
    ],
    chart: {
      type: "line",
      labels: rows.slice(0, 20).reverse().map((_, i) => `#${i + 1}`),
      values: rows.slice(0, 20).reverse().map((r) => Math.round(num(r.quote) * 100)),
      title: t("js.profile.trefferquoteSession"),
    },
  };
}

// Score-Verteilung aus stats_501.score_frequencies
function buildScoreDistribution(freq) {
  if (!freq || typeof freq !== "object") return null;
  const entries = Object.entries(freq)
    .map(([k, v]) => [Number(k), num(v)])
    .filter(([k, v]) => k > 0 && v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .sort((a, b) => a[0] - b[0]);
  if (!entries.length) return null;
  return {
    type: "bar",
    labels: entries.map((e) => e[0]),
    values: entries.map((e) => e[1]),
    title: t("js.profile.007"),
  };
}

// Durchschnitt pro Zielzahl aus stats_secure.number_stats
function buildNumberChart(numberStats) {
  if (!numberStats || typeof numberStats !== "object") return null;
  const labels = [];
  const values = [];
  for (let i = 1; i <= 20; i++) {
    const entry = numberStats[String(i)];
    if (!entry || !num(entry.count)) continue;
    labels.push(String(i));
    values.push(Number((num(entry.points) / num(entry.count)).toFixed(1)));
  }
  ["25", "50"].forEach((k) => {
    const entry = numberStats[k];
    if (entry && num(entry.count)) {
      labels.push(k === "25" ? "BULL" : "B-EYE");
      values.push(Number((num(entry.points) / num(entry.count)).toFixed(1)));
    }
  });
  if (!labels.length) return null;
  return { type: "bar", labels, values, title: t("js.profile.053") };
}

// ------------------------------------------------------------------
// DARSTELLUNG
// ------------------------------------------------------------------
function renderProfileTab(tab, data) {
  const body = document.getElementById("profile-tab-body");
  if (!body) return;
  body.innerHTML = "";

  if (tab.type === "heatmap") {
    destroyProfileChart();
    if (typeof window.renderHeatmapTab === "function") {
      window.renderHeatmapTab(body);
    } else {
      body.innerHTML =
        `<div class="profile-empty">${t("js.profile.028")}</div>`;
    }
    return;
  }

  const isEmpty = tab.type === "single" ? !data : !data || data.length === 0;
  if (isEmpty) {
    // Einheitliches Empty-State-Muster (GUI-Plan P2.5, ui-kit.js)
    if (typeof renderEmptyState === "function") {
      renderEmptyState(body, {
        text: profileView
          ? tf("js.profile.009", { p1: tab.label })
          : tf("js.profile.010", { p1: tab.label }),
        actionLabel: profileView ? null : t("js.profile.054"),
        onAction: profileView
          ? null
          : () => {
              window.location.href = "/";
            },
      });
    } else {
      const empty = document.createElement("div");
      empty.className = "profile-empty";
      empty.textContent = tf("js.profile.011", { p1: tab.label });
      body.appendChild(empty);
    }
    destroyProfileChart();
    return;
  }

  const { kpis, chart } = buildProfileStats(tab.key, data);

  const grid = document.createElement("div");
  grid.className = "profile-kpi-grid";
  kpis.forEach((kpi) => {
    const card = document.createElement("div");
    card.className = "profile-kpi";

    const value = document.createElement("div");
    value.className = "profile-kpi-value";
    if (kpi.accent) value.style.color = kpi.accent;
    value.textContent = kpi.value;

    const label = document.createElement("div");
    label.className = "profile-kpi-label";
    label.textContent = kpi.label;

    card.appendChild(value);
    card.appendChild(label);
    grid.appendChild(card);
  });
  body.appendChild(grid);

  renderProfileChart(chart, body);

  // Erweiterte 501-Statistiken (stats501-erweitert.js): Aktivitaet,
  // Verlaeufe, Verteilungen, Top-10 - berechnet aus match_history_501.
  // Vor der Match-History, damit die Langliste unten bleibt.
  if (tab.key === "501" && typeof window.renderErweiterte501Stats === "function") {
    const erweitert = document.createElement("div");
    erweitert.id = "erweiterte-501-stats";
    body.appendChild(erweitert);
    window.renderErweiterte501Stats(erweitert, data);
  }

  if (tab.type === "rows") renderProfileHistory(data, tab, body);
  else if (PROFILE_MATCH_TABLES[tab.key]) renderProfileMatchHistory(tab, body);
}

// ------------------------------------------------------------------
// MATCH-HISTORY fuer 501 und Secure (wiederhergestellt 13.08.2026)
//
// Die alte Match-History lebte im Statistik-Modal (stats.js,
// loadMatchHistory -> #history-list). Beim Umzug der Statistiken auf die
// eigene Seite (07.08.2026) zogen nur Kennzahlen und Diagramm um - die
// Historie blieb im Modal zurueck, das nirgends mehr geoeffnet wird.
// Die DATEN waren nie weg: die RPC process_match_results schreibt
// match_history_501/match_history_secure unveraendert weiter (am
// 13.08.2026 am Live-Bestand bestaetigt). Hier wird die Anzeige im Stil
// der neuen Seite nachgebaut; die Detailtiefe (Legs samt Aufnahmen bzw.
// Secure-Runden) bleibt erhalten.
//
// Nicht uebernommen: der Loesch-Knopf des alten Modals - er hing an
// deleteUniversalMatch in stats.js samt Modal-Zustand. Falls gewuenscht,
// ist das ein eigener, kleiner Schritt.
// ------------------------------------------------------------------
const PROFILE_MATCH_TABLES = {
  501: "match_history_501",
  secure: "match_history_secure",
};

// Namen und Zahlen kommen aus der Datenbank (auch von fremden Spielern) -
// vor innerHTML gehoeren sie escaped.
function escHtml(v) {
  return String(v ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c],
  );
}

async function loadProfileMatchRows(tabKey) {
  const cacheKey = "history:" + tabKey;
  if (profileData[cacheKey] !== undefined) return profileData[cacheKey];

  try {
    let query = _supabase
      .from(PROFILE_MATCH_TABLES[tabKey])
      .select("*")
      .order("created_at", { ascending: false })
      .limit(25);
    // profileFilter() greift nicht: Diese Tabellen haben player_name
    // statt name als Namensspalte.
    if (profileView) {
      query = profileView.userId
        ? query.eq("user_id", profileView.userId)
        : query.eq("player_name", profileView.name);
    } else {
      query = query.eq("user_id", currentUser.id);
    }
    const { data, error } = await query;
    if (error) throw error;
    profileData[cacheKey] = data || [];
  } catch (err) {
    console.warn(`Match-History ${tabKey}:`, err?.message || err);
    profileData[cacheKey] = [];
  }
  return profileData[cacheKey];
}

// Ein Leg als kompakte Tabelle - dieselben fuenf Spalten wie im alten
// Modal (Rest | Score | Aufnahme-Nr | Score | Rest), damit die Ansicht
// wiedererkennbar bleibt.
function profileLegDetailsHtml(leg, myName) {
  const isP1 = leg.p1_name === myName;
  const myHistory = (isP1 ? leg.p1_history : leg.p2_history) || [];
  const oppHistory = (isP1 ? leg.p2_history : leg.p1_history) || [];
  const myDarts = num(isP1 ? leg.p1_darts : leg.p2_darts);
  const myRest = num(isP1 ? leg.p1_rest : leg.p2_rest);
  const oppRest = num(isP1 ? leg.p2_rest : leg.p1_rest);
  const myScore = 501 - myRest;
  const gewonnen = leg.winner === myName;

  let html = `
    <div class="leg-detail-container" style="margin-top:12px; border-top:1px solid var(--glass-border); padding-top:10px;">
      <div style="text-align:center; font-weight:bold; color:var(--accent-blue); margin-bottom:10px; text-transform:uppercase; letter-spacing:1px;">
        Leg ${num(leg.leg_number)} (${gewonnen ? t("js.profile.029") : t("js.profile.030")})
      </div>
      <div style="display:grid; grid-template-columns:repeat(5,1fr); text-align:center; font-size:0.7em; color:var(--text-muted); margin-bottom:10px; text-transform:uppercase;">
        <div>Darts<br><b style="color:white; font-size:1.3em;">${myDarts}</b></div>
        <div>Avg<br><b style="color:white; font-size:1.3em;">${myDarts > 0 ? ((myScore / myDarts) * 3).toFixed(1) : "0"}</b></div>
        <div>Rest<br><b style="color:var(--accent-blue); font-size:1.3em;">${myRest}</b></div>
        <div>Finish<br><b style="color:white; font-size:1.3em;">${escHtml(leg.checkout || "-")}</b></div>
        <div>Opp. Rest<br><b style="color:white; font-size:1.3em;">${oppRest}</b></div>
      </div>
      <table style="width:100%; table-layout:fixed; font-size:0.85em; border-collapse:collapse; text-align:center;">
        <thead>
          <tr style="color:var(--text-muted); font-size:0.8em; border-bottom:1px solid var(--glass-border); text-transform:uppercase;">
            <th style="padding:6px 0;">Pkt</th><th style="padding:6px 0;">Score</th>
            <th style="padding:6px 0; color:var(--accent-blue);">Aufn.</th>
            <th style="padding:6px 0;">Score</th><th style="padding:6px 0;">Pkt</th>
          </tr>
        </thead>
        <tbody>`;

  const maxTurns = Math.max(myHistory.length, oppHistory.length);
  for (let i = 0; i < maxTurns; i++) {
    html += `
      <tr style="border-bottom:1px solid rgba(255,255,255,0.05);">
        <td style="color:var(--text-muted); padding:6px 0;">${escHtml(myHistory[i]?.old ?? "")}</td>
        <td style="font-weight:bold; color:white; padding:6px 0;">${escHtml(myHistory[i]?.thrown ?? "")}</td>
        <td style="color:var(--text-muted); padding:6px 0;">${i + 1}</td>
        <td style="font-weight:bold; color:white; padding:6px 0;">${escHtml(oppHistory[i]?.thrown ?? "")}</td>
        <td style="color:var(--text-muted); padding:6px 0;">${escHtml(oppHistory[i]?.old ?? "")}</td>
      </tr>`;
  }
  return html + `</tbody></table></div>`;
}

function profileSecureDetailsHtml(m) {
  const runden = Array.isArray(m.match_details) ? m.match_details : [];
  if (!runden.length) {
    return `<div style="color:var(--text-muted); font-size:0.85em; padding-top:8px;">${t("js.profile.034")}</div>`;
  }
  return runden
    .map((round, i) => {
      const targetName =
        round.target === 25 ? "BULL" : round.target === 50 ? "BULLSEYE" : round.target;
      const secureText = round.secured
        ? `<span style="color:var(--accent-green)">Gesichert (x${num(round.multiplier)})</span>`
        : `<span style="color:var(--accent-red)">Verfehlt</span>`;
      const pointsText =
        num(round.final_points) > 0
          ? `<b style="color:var(--accent-blue)">+${num(round.final_points)} Pkt</b>`
          : `<b style="color:var(--text-muted)">0 Pkt</b>`;
      return `
        <div style="display:flex; justify-content:space-between; font-size:0.85em; padding:6px 0; border-bottom:1px solid rgba(255,255,255,0.05);">
          <span style="color:var(--text-muted);">Runde ${i + 1} (Ziel <b style="color:white;">${escHtml(targetName)}</b>)</span>
          <span style="text-align:right;">Wurf: <b style="color:white;">${escHtml(round.raw_score)}</b> &rarr; ${secureText}<br>${pointsText}</span>
        </div>`;
    })
    .join("");
}

async function renderProfileMatchHistory(tab, body) {
  const rows = await loadProfileMatchRows(tab.key);

  // Der Reiter kann waehrend des Ladens gewechselt worden sein - dann
  // gehoert das Ergebnis nicht mehr in den sichtbaren Body.
  if (profileActiveTab !== tab.key || !body.isConnected) return;
  if (!rows.length) return;

  const wrap = document.createElement("div");
  wrap.className = "profile-history";

  const title = document.createElement("div");
  title.className = "profile-chart-title";
  title.textContent = t("js.profile.055");
  wrap.appendChild(title);

  rows.forEach((m) => {
    const date = m.created_at
      ? new Date(m.created_at).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

    const item = document.createElement("div");
    item.className = `history-item ${m.is_win ? "win" : "lose"}`;

    let summaryHtml = "";
    let detailsHtml = "";
    if (tab.key === "501") {
      const legs = Array.isArray(m.match_details) ? m.match_details : [];
      summaryHtml = `
        <div style="flex:1;">
          <div style="font-weight:bold; color:${m.is_win ? "var(--accent-green)" : "var(--accent-red)"}">
            ${m.is_win ? t("js.profile.056") : t("js.profile.057")} gegen <b>${escHtml(m.opponent_name)}</b>
          </div>
          <div class="history-date">${date} | Avg: ${escHtml(m.match_average)} | Legs: ${legs.length}</div>
        </div>`;
      detailsHtml = legs
        .map((leg) => profileLegDetailsHtml(leg, m.player_name))
        .join("");
    } else {
      summaryHtml = `
        <div style="flex:1;">
          <div style="font-weight:bold; color:${m.is_win ? "var(--accent-green)" : "var(--accent-red)"}">
            ${m.is_win ? t("js.profile.031") : t("js.profile.058")}${m.is_training ? ' <span style="color:var(--accent); font-size:0.8em;">(Training)</span>' : ""}
          </div>
          <div class="history-date">${date}</div>
        </div>
        <div style="font-size:1.4em; font-weight:bold; color:white;">
          ${num(m.final_score)} <span style="font-size:0.5em; color:var(--text-muted); font-weight:normal;">Pkt</span>
        </div>`;
      detailsHtml = profileSecureDetailsHtml(m);
    }

    const summary = document.createElement("div");
    summary.className = "history-summary";
    summary.style.cssText =
      "display:flex; align-items:center; gap:10px; cursor:pointer;";
    summary.innerHTML = summaryHtml;

    const details = document.createElement("div");
    details.className = "history-details";
    details.style.display = "none";
    details.innerHTML = detailsHtml;

    // Aufklappen ohne Abhaengigkeit zu stats.js (toggleHistoryDetails
    // lebt dort und ist auf stats.html gar nicht geladen).
    summary.addEventListener("click", () => {
      details.style.display =
        details.style.display === "none" ? "block" : "none";
    });

    item.appendChild(summary);
    item.appendChild(details);
    wrap.appendChild(item);
  });

  body.appendChild(wrap);
}

function destroyProfileChart() {
  if (profileChart) {
    profileChart.destroy();
    profileChart = null;
  }
}

function renderProfileChart(chart, body) {
  destroyProfileChart();
  if (!chart || typeof Chart === "undefined") return;

  const wrap = document.createElement("div");
  wrap.className = "profile-chart-wrap";

  const title = document.createElement("div");
  title.className = "profile-chart-title";
  title.textContent = chart.title;
  wrap.appendChild(title);

  const canvas = document.createElement("canvas");
  canvas.height = 200;
  wrap.appendChild(canvas);
  body.appendChild(wrap);

  profileChart = new Chart(canvas, {
    type: chart.type,
    data: {
      labels: chart.labels,
      datasets: [
        {
          data: chart.values,
          backgroundColor: "rgba(59, 130, 246, 0.45)",
          borderColor: "#3b82f6",
          borderWidth: 2,
          borderRadius: 4,
          tension: 0.3,
          fill: chart.type === "line",
          pointRadius: chart.type === "line" ? 3 : 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        x: { ticks: { color: "#94a3b8" }, grid: { display: false } },
        y: {
          beginAtZero: true,
          // Ohne Zuschlag endet die Skala beim hoechsten Wert. Der groesste
          // Balken stiess dadurch an die Oberkante und wurde von der
          // Zeichenflaeche abgeschnitten - man sah nicht mehr, wie weit er
          // ueber die anderen hinausgeht. 10 % Luft loesen ihn vom Rand.
          grace: "10%",
          ticks: { color: "#94a3b8" },
          grid: { color: "rgba(255,255,255,0.06)" },
        },
      },
    },
  });
}

function renderProfileHistory(rows, tab, body) {
  const wrap = document.createElement("div");
  wrap.className = "profile-history";

  const title = document.createElement("div");
  title.className = "profile-chart-title";
  title.textContent = t("js.profile.008");
  wrap.appendChild(title);

  rows.slice(0, 10).forEach((row) => {
    const line = document.createElement("div");
    line.className = "profile-history-row";

    const left = document.createElement("span");
    left.style.color = "var(--text-muted)";
    left.textContent = row.created_at
      ? new Date(row.created_at).toLocaleDateString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        })
      : "-";

    const right = document.createElement("span");
    right.style.fontWeight = "800";
    right.textContent = profileHistoryValue(tab.key, row);

    line.appendChild(left);
    line.appendChild(right);
    wrap.appendChild(line);
  });

  body.appendChild(wrap);
}

function profileHistoryValue(key, row) {
  if (key === "bobs")
    return tf("js.profile.032", {
      p1: num(row.final_score),
      p2: row.is_win ? t("js.profile.geschafft") : "",
    });
  if (key === "rtw") return tf("js.profile.033", { p1: num(row.total_points), p2: row.mode || "-" });
  if (key === "121") return tf("js.profile.060", { p1: num(row.best_target) });
  if (key === "random")
    return `${num(row.hits)} / ${num(row.attempts)} · ${Math.round(num(row.quote) * 100)} %`;
  if (key === "cricket")
    return `${row.variant || "cricket"}${row.is_solo ? " · Solo" : ""} · ${row.winner_name || "-"}`;
  return "-";
}

function setProfileText(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

// Nach einem Avatar-Upload oder einer Namensaenderung neu zeichnen
function refreshProfileScreen() {
  const screen = document.getElementById("profile-screen");
  if (!screen || screen.style.display !== "block") return;
  renderProfileHeader();
}

// Cache leeren, damit nach einem Spiel frische Zahlen kommen
function invalidateProfileCache() {
  profileData = {};
}

// ---------------------------------------------------------------
// Bruecke zu window: Inline-Handler im HTML und andere Module greifen
// weiterhin global auf diese Funktionen zu. Automatisch erzeugt beim
// Umstieg auf Vite - Modul-Scope ersetzt sonst die alten Globals.
// ---------------------------------------------------------------
Object.assign(window, {
  openProfileScreen,
  openPlayerProfile,
  closeProfileScreen,
  refreshProfileScreen,
  invalidateProfileCache,
  // Neu 07.08.2026 (Aufgabe 3): Die Kennzahlen-Bausteine sind wiederverwendbar -
  // stats.html (eigene Statistikseite) und das Freunde-Stats-Modal (friends.js)
  // bauen darauf auf, statt eigene Berechnungen zu pflegen.
  buildProfileStats,
  renderProfileTabs,
  switchProfileTab,
  loadProfileTotals,
});
